import React, { useState, useRef, useEffect } from "react";
// import { Link } from "react-router-dom";
import logoImage from "./logo-png.png";
import { useForm, ValidationError } from "@formspree/react";

const SlotMachineDonation = ({
  logoUrl = logoImage,
  goalAmount = 250000,
  initialAmount = 200000,
}) => {
  // Donation state (from v0)
  const [donationAmount, setDonationAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState("");
  const [isMonthly, setIsMonthly] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showJackpot, setShowJackpot] = useState(false);
  const [reelValues, setReelValues] = useState([0, 0, 0]);
  const [reelOffsets, setReelOffsets] = useState([0, 0, 0]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentAmount, setCurrentAmount] = useState(initialAmount);
  const [chipsGoingCrazy, setChipsGoingCrazy] = useState(false);

  // Help form state (from v1)
  const [helpState, helpHandleSubmit] = useForm("meolnvkp");
  const [helpForm, setHelpForm] = useState({
    name: "",
    condition: "",
    situation: "",
    file: null,
  });

  const fileInputRef = useRef(null);
  const presetAmounts = [10, 25, 50, 100];

  // Casino-themed symbols for Ferda Kids (from v0)
  const slotSymbols = [
    { icon: "💜", name: "Heart" },
    { icon: "⭐", name: "Star" },
    { icon: "💎", name: "Diamond" },
    { icon: "🎗️", name: "Ribbon" },
    { icon: "🎰", name: "Slots" },
    { icon: "🔥", name: "Fire" },
    { icon: "🏆", name: "Trophy" },
    { icon: "🎯", name: "Target" },
  ];

  // Special jackpot symbol (from v0)
  // const jackpotSymbol = { icon: "FK", name: "Ferda Kids" };

  const getCurrentAmount = () => {
    return currentAmount;
  };

  const getProgressPercentage = () => {
    return Math.min((currentAmount / goalAmount) * 100, 100);
  };

  // Audio functions (from v0)
  const playSpinSound = () => {
    try {
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(
        400,
        audioContext.currentTime + 0.1
      );

      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.1
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.log("Audio not supported");
    }
  };

  const playWinSound = () => {
    try {
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(
        880,
        audioContext.currentTime + 0.2
      );

      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.2
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      console.log("Audio not supported");
    }
  };

  // Slot machine functions (from v0)
  const getReelSymbol = (reelIndex) => {
    if (isSpinning) {
      return slotSymbols[reelOffsets[reelIndex] % slotSymbols.length];
    }
    return slotSymbols[reelValues[reelIndex] % slotSymbols.length];
  };

  const spinReel = (reelIndex, duration, finalValue) => {
    const startTime = Date.now();
    const spinInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = elapsed / duration;

      if (progress >= 1) {
        clearInterval(spinInterval);
        // Final stop - set both offsets and values
        setReelOffsets((prev) => {
          const newOffsets = [...prev];
          newOffsets[reelIndex] = finalValue;
          return newOffsets;
        });
        setReelValues((prev) => {
          const newValues = [...prev];
          newValues[reelIndex] = finalValue;
          return newValues;
        });
      } else {
        // Continue spinning
        setReelOffsets((prev) => {
          const newOffsets = [...prev];
          newOffsets[reelIndex] = Math.floor(
            Math.random() * slotSymbols.length
          );
          return newOffsets;
        });
      }
    }, 100);
  };

  const handleDonate = async (amount) => {
    if (isSpinning) return;

    setIsSpinning(true);
    setChipsGoingCrazy(true);
    playSpinSound();

    // Reset success states
    setShowSuccess(false);
    setShowJackpot(false);
    setShowConfetti(false);

    // Spin all reels
    const spinDuration = 2000 + Math.random() * 1000;
    const finalValues = [0, 0, 0];

    // Determine if this is a jackpot (10% chance)
    const isJackpot = Math.random() < 0.1;
    if (isJackpot) {
      finalValues[0] = finalValues[1] = finalValues[2] = slotSymbols.length; // FK symbol
    } else {
      // Random symbols
      finalValues[0] = Math.floor(Math.random() * slotSymbols.length);
      finalValues[1] = Math.floor(Math.random() * slotSymbols.length);
      finalValues[2] = Math.floor(Math.random() * slotSymbols.length);
    }

    // Start spinning
    spinReel(0, spinDuration, finalValues[0]);
    setTimeout(() => spinReel(1, spinDuration, finalValues[1]), 200);
    setTimeout(() => spinReel(2, spinDuration, finalValues[2]), 400);

    // Wait for spin to complete
    setTimeout(() => {
      setIsSpinning(false);
      setChipsGoingCrazy(false);

      if (isJackpot) {
        setShowJackpot(true);
        setShowConfetti(true);
        playWinSound();
        setTimeout(() => setShowConfetti(false), 3000);
      } else {
        setShowSuccess(true);
        playWinSound();
      }

      // Update current amount
      setCurrentAmount((prev) => prev + amount);

      // Hide success messages after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        setShowJackpot(false);
      }, 3000);
    }, spinDuration + 500);
  };

  // Help form functions (from v1)
  const handleHelpInputChange = (field, value) => {
    setHelpForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setHelpForm((prev) => ({
      ...prev,
      file: file,
    }));
  };

  // Confetti component (from v0)
  const ConfettiPiece = ({ delay }) => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
      const timer = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(timer);
    }, [delay]);

    if (!visible) return null;

    return (
      <div
        className="fixed pointer-events-none z-50"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 2}s`,
          animationDuration: `${2000 + Math.random() * 1000}ms`,
        }}
      >
        <div
          className={`w-2 h-2 rounded-full ${
            Math.random() > 0.5 ? "bg-purple-400" : "bg-pink-400"
          } animate-bounce`}
          style={{
            animationDuration: `${2000 + Math.random() * 1000}ms`,
          }}
        />
      </div>
    );
  };

  return (
    <div className="relative overflow-hidden w-full">
      {/* Subtle confetti for jackpot */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {[...Array(15)].map((_, i) => (
            <ConfettiPiece key={i} delay={i * 100} />
          ))}
        </div>
      )}

      {/* Instagram Contact Notice */}
      <div className="max-w-5xl w-full mx-auto mb-6">
        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-2 border-yellow-400/50 rounded-2xl p-4 backdrop-blur-sm">
          <div className="text-center">
            <div className="text-2xl mb-2">📱</div>
            <p className="text-white text-sm mb-1">
              For inquiries about this site, or if you'd like me to make you
              your own site, please DM me on Instagram at{" "}
              <span className="text-yellow-400 font-semibold">@tqrqn</span>.
            </p>
            <p className="text-gray-300 text-xs">
              Note: none of the other contact links on this site are active.
              This is the only way to reach me.
            </p>
          </div>
        </div>
      </div>

      {/* Two column layout */}
      <div className="flex flex-row gap-6 max-w-5xl w-full relative z-10 mx-auto">
        {/* LEFT SIDE - HELP REQUEST FORM (from v1) */}
        <div className="flex-1">
          <div className="bg-gradient-to-b from-purple-400 to-purple-600 p-2 rounded-3xl shadow-2xl shadow-purple-400/30 relative h-full">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 opacity-30 animate-pulse"></div>
            <div className="bg-black bg-opacity-95 backdrop-blur-sm rounded-2xl p-6 relative h-full flex flex-col">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white mb-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Need Help?
                </h2>
                <p className="text-gray-300 text-sm">
                  plz email us or submit a request below if u have cancer and
                  are a kid and also need ur medical bills paid by a max win
                </p>
              </div>

              {helpState.succeeded ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-6xl mb-4">💜</div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      Request Submitted!
                    </h3>
                    <p className="text-purple-200 text-sm">
                      We'll review your request and get back to you soon.
                    </p>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={helpHandleSubmit}
                  className="flex-1 flex flex-col"
                >
                  <div className="space-y-4 flex-1">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">
                        Your Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={helpForm.name}
                        onChange={(e) =>
                          handleHelpInputChange("name", e.target.value)
                        }
                        className="w-full px-3 py-2 bg-gray-800 border-2 border-gray-600 rounded-lg text-white focus:border-purple-400 transition-colors"
                        placeholder="Enter your name"
                      />
                      <ValidationError
                        prefix="Name"
                        field="name"
                        errors={helpState.errors}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">
                        Your Condition
                      </label>
                      <input
                        type="text"
                        name="condition"
                        required
                        value={helpForm.condition}
                        onChange={(e) =>
                          handleHelpInputChange("condition", e.target.value)
                        }
                        className="w-full px-3 py-2 bg-gray-800 border-2 border-gray-600 rounded-lg text-white focus:border-purple-400 transition-colors"
                        placeholder="Type of cancer or condition"
                      />
                      <ValidationError
                        prefix="Condition"
                        field="condition"
                        errors={helpState.errors}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">
                        Upload Medical Bills
                      </label>
                      <div className="relative">
                        <input
                          ref={fileInputRef}
                          type="file"
                          onChange={handleFileChange}
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          className="hidden"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full px-3 py-2 bg-gray-800 border-2 border-gray-600 rounded-lg text-left text-gray-400 hover:border-purple-400 transition-colors"
                        >
                          {helpForm.file
                            ? helpForm.file.name
                            : "Click to upload bills/documents..."}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-1">
                        How Can We Help?
                      </label>
                      <textarea
                        name="situation"
                        required
                        rows="3"
                        value={helpForm.situation}
                        onChange={(e) =>
                          handleHelpInputChange("situation", e.target.value)
                        }
                        className="w-full px-3 py-2 bg-gray-800 border-2 border-gray-600 rounded-lg text-white focus:border-purple-400 transition-colors resize-none"
                        placeholder="Tell us about your situation and how we can help..."
                      />
                      <ValidationError
                        prefix="Situation"
                        field="situation"
                        errors={helpState.errors}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={helpState.submitting}
                    className={`w-full py-4 rounded-xl font-black text-xl transition-all ${
                      helpState.submitting
                        ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 text-white shadow-xl hover:scale-105"
                    }`}
                  >
                    {helpState.submitting
                      ? "SUBMITTING..."
                      : "💜 SUBMIT REQUEST 💜"}
                  </button>
                </form>
              )}

              <div className="mt-6 text-center">
                <div className="bg-purple-900 bg-opacity-50 border border-purple-400 rounded-lg p-3">
                  <p className="text-purple-300 text-xs mb-1">
                    Need help? We're here for you.
                  </p>
                  <p className="text-yellow-400 font-mono text-xs mb-1">
                    contact@maxwinsferdakids.org
                  </p>
                  <p className="text-purple-400 text-xs">
                    Kids with cancer • Medical bill support
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - ADVANCED SLOT MACHINE (from v0) */}
        <div className="flex-1">
          <div className="bg-gradient-to-b from-yellow-400 to-yellow-600 p-2 rounded-3xl shadow-2xl shadow-yellow-400/30 relative">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-pink-500 via-yellow-500 to-cyan-500 opacity-30 animate-pulse"></div>
            <div className="bg-black bg-opacity-95 backdrop-blur-sm rounded-2xl p-6 relative">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white mb-2 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                  Choose Your Bet
                </h2>
                <p className="text-gray-300 text-sm">
                  Help kids fighting cancer. Pull the lever to donate!
                </p>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-300 mb-2">
                  <span>${getCurrentAmount().toLocaleString()} raised</span>
                  <span>${goalAmount.toLocaleString()} goal</span>
                </div>
                <div className="h-4 bg-gray-700 rounded-full overflow-hidden border-2 border-gray-600">
                  <div
                    className="h-full bg-gradient-to-r from-green-400 via-yellow-400 to-orange-500 transition-all duration-1000 ease-out relative overflow-hidden"
                    style={{ width: `${getProgressPercentage()}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                  </div>
                </div>
                <p className="text-center text-xs text-gray-400 mt-2">
                  {Math.round(getProgressPercentage())}% complete
                </p>
              </div>

              {/* Donation amounts */}
              <div className="mb-6">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {presetAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => {
                        setDonationAmount(amount);
                        setCustomAmount("");
                      }}
                      className={`px-3 py-2 rounded-lg font-bold text-sm transition-all duration-200 border-2 ${
                        donationAmount === amount && !customAmount
                          ? "bg-gradient-to-r from-green-400 to-blue-500 border-green-400 text-white shadow-lg shadow-green-400/30 scale-105"
                          : "bg-gray-800 border-gray-600 text-white hover:border-yellow-400 hover:bg-gray-700 hover:scale-105"
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>

                <input
                  type="number"
                  placeholder="Custom amount"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    if (e.target.value) {
                      setDonationAmount(parseInt(e.target.value) || 0);
                    }
                  }}
                  className="w-full px-3 py-2 mb-3 rounded-lg bg-gray-800 border-2 border-gray-600 text-white text-sm font-semibold placeholder-gray-400 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/20"
                  min="1"
                />
              </div>

              <div className="flex items-center justify-center mb-6">
                <button
                  onClick={() => setIsMonthly(false)}
                  className={`px-4 py-2 rounded-l-lg font-semibold text-sm transition-all ${
                    !isMonthly
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  One-time
                </button>
                <button
                  onClick={() => setIsMonthly(true)}
                  className={`px-4 py-2 rounded-r-lg font-semibold text-sm transition-all ${
                    isMonthly
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  Monthly
                </button>
              </div>

              {/* Advanced Slot Machine */}
              <div className="mb-6">
                <div className="bg-gradient-to-b from-red-600 to-red-800 p-1 rounded-xl mb-4 relative">
                  {/* Neon glow effect */}
                  <div className="absolute inset-0 rounded-xl bg-red-500 blur-sm opacity-50 animate-pulse"></div>

                  <div className="bg-black rounded-lg p-4 relative">
                    {/* Casino-style top banner */}
                    <div className="text-center mb-3">
                      <div className="text-yellow-400 font-bold text-xs tracking-wider">
                        ★ FERDA KIDS JACKPOT ★
                      </div>
                    </div>

                    {/* Reels */}
                    <div className="flex justify-center items-center space-x-3">
                      {[0, 1, 2].map((reelIndex) => (
                        <div
                          key={reelIndex}
                          className="relative w-16 h-16 bg-white rounded-lg flex items-center justify-center border-4 border-yellow-400 overflow-hidden"
                        >
                          {/* Spinning overlay */}
                          {isSpinning && (
                            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white to-transparent opacity-50 animate-spin-fast"></div>
                          )}

                          {/* Symbol display */}
                          <div
                            className={`text-2xl font-bold transition-all duration-200 ${
                              isSpinning ? "animate-bounce" : ""
                            }`}
                          >
                            {isSpinning
                              ? slotSymbols[
                                  reelOffsets[reelIndex] % slotSymbols.length
                                ]?.icon || "🎰"
                              : (() => {
                                  const symbol = getReelSymbol(reelIndex);
                                  return symbol.icon === "FK" ? (
                                    <div className="text-xs font-black text-purple-600 transform rotate-12">
                                      FK
                                    </div>
                                  ) : (
                                    symbol.icon
                                  );
                                })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Donate Button */}
              <button
                onClick={() => handleDonate(donationAmount)}
                disabled={isSpinning || donationAmount <= 0}
                className={`w-full py-4 px-6 rounded-xl font-black text-xl transition-all duration-300 transform relative overflow-hidden ${
                  isSpinning || donationAmount <= 0
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 hover:from-yellow-300 hover:via-orange-400 hover:to-red-400 text-white shadow-2xl shadow-orange-500/40 hover:scale-105 hover:shadow-orange-500/60 animate-pulse"
                }`}
              >
                {/* Button shine effect */}
                {!isSpinning && donationAmount > 0 && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-shine"></div>
                )}

                {isSpinning ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>SPINNING...</span>
                  </div>
                ) : (
                  // `🎰 PULL TO DONATE $${donationAmount} 🎰`
                  `🎰 HELP KIDS BEAT CANCER 💜`
                )}
              </button>

              {donationAmount <= 0 && (
                <p className="text-red-400 text-center mt-2 text-xs">
                  Select your bet to play! 🎲
                </p>
              )}

              {/* Footer */}
              <div className="text-center mt-4">
                <p className="text-gray-400 text-xs mb-1">
                  {showJackpot ? (
                    <span className="text-purple-300">
                      💜 Thank you for donating ${donationAmount}! 💜
                    </span>
                  ) : showSuccess ? (
                    <span className="text-green-300">
                      💚 Thank you for donating ${donationAmount}! 💚
                      {isMonthly && " (Monthly)"}
                    </span>
                  ) : (
                    "🎯 Every donation is a winning bet for these kids! 🎯"
                  )}
                </p>
                <div className="flex justify-center space-x-1 text-yellow-400 animate-pulse">
                  <span>⭐</span>
                  <span>⭐</span>
                  <span>⭐</span>
                  <span>⭐</span>
                  <span>⭐</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlotMachineDonation;
